'use client';

import { useClientLogger } from '@coolblue-development/next-config/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { useTranslation } from '@i18n/useTranslation';
import { Filters } from '@server/careers-backend/search';
import { SEARCH_QUERY_PARAM } from '@utils/constants';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  ReactNode,
  startTransition,
  useActionState,
  useEffect,
  useMemo,
  useState
} from 'react';

import { revalidatePathAction } from './revalidatePathAction';

type FiltersContextProps = {
  isPending: boolean;
  getSelectedFilterOptions: (name: keyof Filters) => string[];
  getSearchQuery: () => string;
  appliedFilters: Record<keyof Filters | keyof Pagination, string[]>;
  setFilter: (category: keyof Filters, values: string[] | undefined) => void;
  setAllFilters: (filters: Record<keyof Filters | keyof Pagination, string[] | undefined>) => void;
  setQuery: (query: string) => void;
  clearAllSearchParams: () => void;
  clearSelectedFilters: () => void;
  activePage: number;
  updateActivePage: (page: number) => void;
};

const DEFAULT_PAGE = 1;

const initialState: FiltersContextProps = {
  isPending: false,
  getSelectedFilterOptions: () => [],
  getSearchQuery: () => '',
  appliedFilters: {} as Record<keyof Filters | keyof Pagination, string[]>,
  setFilter: () => {},
  setAllFilters: () => {},
  setQuery: () => {},
  clearAllSearchParams: () => {},
  clearSelectedFilters: () => {},
  activePage: DEFAULT_PAGE,
  updateActivePage: () => {}
};

type FiltersContextProviderProps = {
  initialActivePage?: number;
};

type Pagination = {
  page: string;
};

type SearchQueryParam = {
  [SEARCH_QUERY_PARAM]: string;
};

export const FiltersContext = createContext<FiltersContextProps>(initialState);

export const FiltersContextProvider = ({
  children,
  initialActivePage = DEFAULT_PAGE
}: FiltersContextProviderProps & { children: ReactNode }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const clientLogger = useClientLogger();
  const { isInternalUser } = useRequestContext();
  const { t } = useTranslation('filterPage');

  // An action  hook keeps track of the queue and executes them in order so the result of the previous call is passed
  const [_, pushSearchParamsAction, isPending] = useActionState(
    async (
      prevState: URLSearchParams,
      payload: {
        newSearchParams: URLSearchParams;
      }
    ) => {
      const { newSearchParams } = payload;

      const newSearch = newSearchParams.toString();
      const oldUrl = prevState ? `${pathname}?${prevState.toString()}` : pathname;
      const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;

      // No need to redirect if the search params are the same
      if (prevState.toString() === newSearchParams.toString()) {
        clientLogger.info('Not navigating, Search params are the same', {
          oldUrl,
          newUrl,
          internal_user: isInternalUser
        });
        return prevState;
      }

      clientLogger.info('Navigating to:', { oldUrl, newUrl, internal_user: isInternalUser });
      try {
        router.push(newUrl, { scroll: false });
      } catch (e) {
        const err = e as Error;
        clientLogger.error('Failed to navigate to new URL', err, {
          oldUrl,
          newUrl,
          internal_user: isInternalUser,
          error: err.message
        });
      }

      await revalidatePathAction(newUrl);
      clientLogger.info('Navigated to:', { oldUrl, newUrl, internal_user: isInternalUser });

      return newSearchParams;
    },
    new URLSearchParams(searchParams.toString())
  );

  /* Preventing losing selected filters when selecting is faster than navigating */
  const [storedSearchParams, setStoredSearchParams] = useState(
    new URLSearchParams(searchParams.toString())
  );

  useEffect(() => {
    const handlePopState = () => {
      setStoredSearchParams(new URLSearchParams(window.location.search.replace('?', '')));
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [activePage, setActivePage] = useState(initialActivePage);

  const pageQueryParamKey = t('page');

  const setSearchParams = (
    filters:
      | {
          key: keyof Filters | keyof Pagination | keyof SearchQueryParam;
          values: string[] | undefined;
        }[]
      | null,
    replaceAll = false
  ) => {
    const baseSearch = replaceAll ? '' : storedSearchParams.toString();
    const params = new URLSearchParams(baseSearch);

    if (filters) {
      for (const { key, values } of filters) {
        params.delete(key);

        if (values && values.length > 0) {
          for (const value of values) {
            params.append(key, value);
          }
        }
      }
    }

    const newSearchParams = new URLSearchParams(params.toString());
    setStoredSearchParams(newSearchParams);

    startTransition(() => {
      pushSearchParamsAction({ newSearchParams });
    });
  };

  // Set a single filter category
  const setFilter = (category: keyof Filters, values: string[] | undefined) => {
    setActivePage(DEFAULT_PAGE);
    setSearchParams([
      { key: category, values },
      { key: pageQueryParamKey as keyof Pagination, values: [DEFAULT_PAGE.toString()] }
    ]);
  };

  // Set all filters at once
  const setAllFilters = (
    filters: Record<keyof Filters | keyof Pagination, string[] | undefined>
  ) => {
    const { page, ...filterValues } = filters;
    const pageValue = page ? Number(page[0]) : DEFAULT_PAGE;

    setActivePage(pageValue);
    setSearchParams(
      [
        ...Object.entries(filterValues).map(([key, values]) => ({
          key: key as keyof Filters,
          values
        })),
        { key: pageQueryParamKey as keyof Pagination, values: [pageValue.toString()] }
      ],
      true
    );
  };

  const setQuery = (query: string) => {
    setActivePage(DEFAULT_PAGE);
    setSearchParams([
      { key: SEARCH_QUERY_PARAM, values: query ? [query] : undefined },
      { key: pageQueryParamKey as keyof Pagination, values: [DEFAULT_PAGE.toString()] }
    ]);
  };

  const getSelectedFilterOptions = (name: keyof Filters) => {
    return storedSearchParams.getAll(name).map(item => item.toLowerCase());
  };

  const getSearchQuery = () => storedSearchParams.get(SEARCH_QUERY_PARAM) ?? '';

  const appliedFilters = useMemo(() => {
    return Array.from(storedSearchParams).reduce(
      (acc, [key, value]) => {
        if (key === SEARCH_QUERY_PARAM) return acc;
        if (key === pageQueryParamKey) {
          acc.page = [value];
          return acc;
        }
        const filterKey = key as keyof Filters;
        acc[filterKey] = acc[filterKey] || [];
        acc[filterKey].push(value.toLowerCase());
        return acc;
      },
      {} as Record<keyof Filters | keyof Pagination, string[]>
    );
  }, [storedSearchParams, pageQueryParamKey]);

  const clearAllSearchParams = () => {
    setSearchParams(null, true);
    setActivePage(DEFAULT_PAGE);
  };

  const clearSelectedFilters = () => {
    const query = storedSearchParams.get(SEARCH_QUERY_PARAM);
    setActivePage(DEFAULT_PAGE);
    setSearchParams(query ? [{ key: SEARCH_QUERY_PARAM, values: [query] }] : null, true);
  };

  const updateActivePage = (page: number) => {
    setActivePage(page);
    setSearchParams([{ key: pageQueryParamKey as keyof Pagination, values: [page.toString()] }]);
  };

  return (
    <FiltersContext.Provider
      value={{
        isPending,
        getSelectedFilterOptions,
        getSearchQuery,
        appliedFilters,
        setFilter,
        setAllFilters,
        setQuery,
        clearAllSearchParams,
        clearSelectedFilters,
        activePage,
        updateActivePage
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
